import React, { useMemo } from 'react';
import { ReactComponent as ArrowCircleSvg } from 'assets/icons/new/arrow-circle.svg';
import { ReactComponent as BookSvg } from 'assets/icons/new/book.svg';
import { ReactComponent as BrandLinkedinSvg } from 'assets/icons/new/brand-linkedin.svg';
import { ReactComponent as BulbElectroSvg } from 'assets/icons/new/bulb-electro.svg';
import { ReactComponent as BulletSvg } from 'assets/icons/new/bullet.svg';
import { ReactComponent as CalendarDateSvg } from 'assets/icons/new/calendar-date.svg';
import { ReactComponent as CalendarFullSvg } from 'assets/icons/new/calendar-full.svg';
import { ReactComponent as ChecklistSvg } from 'assets/icons/new/checklist.svg';
import { ReactComponent as CloudMessageZigzagIcon } from 'assets/icons/new/cloud-message-zigzag.svg';
import { ReactComponent as ClockSvg } from 'assets/icons/new/clock.svg';
import { ReactComponent as CommunicationSvg } from 'assets/icons/new/communication.svg';
import { ReactComponent as ContactSvg } from 'assets/icons/new/contact.svg';
import { ReactComponent as DesktopSvg } from 'assets/icons/new/desctop.svg';
import { ReactComponent as EyeSvg } from 'assets/icons/new/eye.svg';
import { ReactComponent as FileBookSvg } from 'assets/icons/new/file-book.svg';
import { ReactComponent as FileCheckSvg } from 'assets/icons/new/file-check.svg';
import { ReactComponent as FileChecklistSvg } from 'assets/icons/new/file-checklist.svg';
import { ReactComponent as FileEditSvg } from 'assets/icons/new/file-edit.svg';
import { ReactComponent as FileExternalSvg } from 'assets/icons/new/file-external.svg';
import { ReactComponent as FileNotFoundSvg } from 'assets/icons/new/file-not-found.svg';
import { ReactComponent as FilePdfSvg } from 'assets/icons/new/file-pdf.svg';
import { ReactComponent as FileSignSvg } from 'assets/icons/new/file-sign.svg';
import { ReactComponent as FileTextWriteSvg } from 'assets/icons/new/file-text-write.svg';
import { ReactComponent as FileTextSvg } from 'assets/icons/new/file-text.svg';
import { ReactComponent as HandSvg } from 'assets/icons/new/hand.svg';
import { ReactComponent as HeartSvg } from 'assets/icons/new/heart.svg';
import { ReactComponent as HomeSvg } from 'assets/icons/new/home.svg';
import { ReactComponent as IdentifierSvg } from 'assets/icons/new/identifier.svg';
import { ReactComponent as PapersSvg } from 'assets/icons/new/papers.svg';
import { ReactComponent as PathSvg } from 'assets/icons/new/path.svg';
import { ReactComponent as QuestionMarkSquareSvg } from 'assets/icons/new/question-mark-square.svg';
import { ReactComponent as SettingsSvg } from 'assets/icons/new/settings.svg';
import { ReactComponent as SheetSvg } from 'assets/icons/new/sheet.svg';
import { ReactComponent as SquareCompasIcon } from 'assets/icons/new/square-compas.svg';
import { ReactComponent as SquareFaceSadIcon } from 'assets/icons/new/square-face-sad.svg';
import { ReactComponent as SquareRocketIcon } from 'assets/icons/new/square-rocket.svg';
import { ReactComponent as SquareSignpostsIcon } from 'assets/icons/new/square-signposts.svg';
import { ReactComponent as SquareSproutIcon } from 'assets/icons/new/square-sprout.svg';
import { ReactComponent as StarSvg } from 'assets/icons/new/star.svg';
import { ReactComponent as StatsSvg } from 'assets/icons/new/stats.svg';
import { ReactComponent as TrashSvg } from 'assets/icons/new/trash.svg';
import { ReactComponent as UserPlusSvg } from 'assets/icons/new/user-plus.svg';
import { ReactComponent as UserSvg } from 'assets/icons/new/user.svg';
import { ReactComponent as UsersSvg } from 'assets/icons/new/users.svg';
import { ReactComponent as VideoSvg } from 'assets/icons/new/video.svg';

type IconName =
	| 'arrow-circle'
	| 'book'
	| 'brand-linkedin'
	| 'bulb-electro'
	| 'bullet'
	| 'calendar-date'
	| 'calendar-full'
	| 'checklist'
	| 'cloud-message-zigzag'
	| 'clock'
	| 'communication'
	| 'contact'
	| 'desktop'
	| 'eye'
	| 'file-book'
	| 'file-check'
	| 'file-checklist'
	| 'file-edit'
	| 'file-external'
	| 'file-not-found'
	| 'file-pdf'
	| 'file-sign'
	| 'file-text-write'
	| 'file-text'
	| 'hand'
	| 'heart'
	| 'home'
	| 'identifier'
	| 'papers'
	| 'path'
	| 'question-mark-square'
	| 'settings'
	| 'sheet'
	| 'square-compas'
	| 'square-face-sad'
	| 'square-rocket'
	| 'square-signposts'
	| 'square-sprout'
	| 'star'
	| 'stats'
	| 'trash'
	| 'user-plus'
	| 'user'
	| 'users'
	| 'video';

type IconSvgProps = React.SVGProps<SVGSVGElement> & {
	name: IconName;
	size?: number | [number, number];
	className?: string;
};

export const IconSvg = ({ name, size, className }: IconSvgProps) => {
	const Component = useMemo(() => {
		switch (name) {
			case 'arrow-circle':
				return ArrowCircleSvg;
			case 'book':
				return BookSvg;
			case 'brand-linkedin':
				return BrandLinkedinSvg;
			case 'bulb-electro':
				return BulbElectroSvg;
			case 'bullet':
				return BulletSvg;
			case 'calendar-date':
				return CalendarDateSvg;
			case 'calendar-full':
				return CalendarFullSvg;
			case 'checklist':
				return ChecklistSvg;
			case 'cloud-message-zigzag':
				return CloudMessageZigzagIcon;
			case 'clock':
				return ClockSvg;
			case 'communication':
				return CommunicationSvg;
			case 'contact':
				return ContactSvg;
			case 'desktop':
				return DesktopSvg;
			case 'eye':
				return EyeSvg;
			case 'file-book':
				return FileBookSvg;
			case 'file-check':
				return FileCheckSvg;
			case 'file-checklist':
				return FileChecklistSvg;
			case 'file-edit':
				return FileEditSvg;
			case 'file-external':
				return FileExternalSvg;
			case 'file-not-found':
				return FileNotFoundSvg;
			case 'file-pdf':
				return FilePdfSvg;
			case 'file-sign':
				return FileSignSvg;
			case 'file-text-write':
				return FileTextWriteSvg;
			case 'file-text':
				return FileTextSvg;
			case 'hand':
				return HandSvg;
			case 'heart':
				return HeartSvg;
			case 'home':
				return HomeSvg;
			case 'identifier':
				return IdentifierSvg;
			case 'papers':
				return PapersSvg;
			case 'path':
				return PathSvg;
			case 'question-mark-square':
				return QuestionMarkSquareSvg;
			case 'settings':
				return SettingsSvg;
			case 'sheet':
				return SheetSvg;
			case 'square-compas':
				return SquareCompasIcon;
			case 'square-face-sad':
				return SquareFaceSadIcon;
			case 'square-rocket':
				return SquareRocketIcon;
			case 'square-signposts':
				return SquareSignpostsIcon;
			case 'square-sprout':
				return SquareSproutIcon;
			case 'star':
				return StarSvg;
			case 'stats':
				return StatsSvg;
			case 'trash':
				return TrashSvg;
			case 'user-plus':
				return UserPlusSvg;
			case 'user':
				return UserSvg;
			case 'users':
				return UsersSvg;
			case 'video':
				return VideoSvg;
			default:
				// eslint-disable-next-line react/function-component-definition
				return () => <span />;
		}
	}, [name]);

	if (typeof size === 'number') {
		return <Component width={size} height={size} className={className} />;
	}
	if (size && Array.isArray(size)) {
		const [width, height] = size;
		return <Component width={width} height={height} className={className} />;
	}
	return <Component className={className} />;
};
